import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';

const EMPTY = {
  id: null,
  consumer_unit_id: '',
  tipo_cobranca: 'FIXO',
  mensalidade_fixa: '',
  valor_por_kwh: '',
  minimo_mensal: '',
  maximo_mensal: '',
  dia_vencimento: 10,
  juros_percentual: 0,
  multa_percentual: 0,
  inicio_vigencia: '',
  fim_vigencia: '',
  ativo: true,
};

const toDateInputValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  return text.slice(0, 10);
};

const SolarContractsPage = () => {
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({
    search: '',
    status: 'TODOS',
    tipo_cobranca: 'TODOS',
  });

  const loadLookups = async () => {
    try {
      const { data } = await axios.get('/api/solar/consumer-units');
      setUnits(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar unidades consumidoras');
    }
  };

  const loadContracts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/solar/contracts');
      const rows = Array.isArray(data?.items) ? data.items : [];
      setItems(
        rows.map((item) => ({
          ...item,
          inicio_vigencia: toDateInputValue(item.inicio_vigencia),
          fim_vigencia: toDateInputValue(item.fim_vigencia),
        }))
      );
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLookups();
    loadContracts();
  }, []);

  const filteredItems = useMemo(() => {
    const search = String(filters.search || '').trim().toLowerCase();
    return items.filter((item) => {
      if (filters.status === 'ATIVOS' && item.ativo !== true) return false;
      if (filters.status === 'INATIVOS' && item.ativo !== false) return false;
      if (filters.tipo_cobranca !== 'TODOS' && item.tipo_cobranca !== filters.tipo_cobranca) return false;
      if (!search) return true;

      const haystack = [item.apelido, item.numero_instalacao_enel, item.customer_nome].map((value) => String(value || '').toLowerCase()).join(' ');
      return haystack.includes(search);
    });
  }, [filters.search, filters.status, filters.tipo_cobranca, items]);

  const openNew = () => {
    setForm(EMPTY);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      consumer_unit_id: item.consumer_unit_id,
      tipo_cobranca: item.tipo_cobranca,
      mensalidade_fixa: item.mensalidade_fixa ?? '',
      valor_por_kwh: item.valor_por_kwh ?? '',
      minimo_mensal: item.minimo_mensal ?? '',
      maximo_mensal: item.maximo_mensal ?? '',
      dia_vencimento: item.dia_vencimento || 10,
      juros_percentual: item.juros_percentual ?? 0,
      multa_percentual: item.multa_percentual ?? 0,
      inicio_vigencia: toDateInputValue(item.inicio_vigencia),
      fim_vigencia: toDateInputValue(item.fim_vigencia),
      ativo: item.ativo !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      const payload = {
        consumer_unit_id: form.consumer_unit_id,
        tipo_cobranca: form.tipo_cobranca,
        mensalidade_fixa: form.mensalidade_fixa === '' ? null : Number(form.mensalidade_fixa),
        valor_por_kwh: form.valor_por_kwh === '' ? null : Number(form.valor_por_kwh),
        minimo_mensal: form.minimo_mensal === '' ? null : Number(form.minimo_mensal),
        maximo_mensal: form.maximo_mensal === '' ? null : Number(form.maximo_mensal),
        dia_vencimento: Number(form.dia_vencimento),
        juros_percentual: Number(form.juros_percentual || 0),
        multa_percentual: Number(form.multa_percentual || 0),
        inicio_vigencia: form.inicio_vigencia || null,
        fim_vigencia: form.fim_vigencia || null,
        ativo: form.ativo,
      };

      if (!payload.consumer_unit_id) {
        toast.error('Selecione a unidade consumidora');
        return;
      }

      if (form.id) {
        await axios.put(`/api/solar/contracts/${form.id}`, payload);
        toast.success('Contrato atualizado');
      } else {
        await axios.post('/api/solar/contracts', payload);
        toast.success('Contrato criado');
      }

      setShowModal(false);
      setForm(EMPTY);
      await loadContracts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar contrato');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solar · Contratos</h1>
          <p className="text-gray-600">Parametros de cobranca por unidade consumidora</p>
        </div>
        <div className="flex items-center gap-3">
          {loading && <div className="loader h-6 w-6" />}
          <button className="btn-primary" onClick={openNew}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Novo Contrato
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-5 gap-3 border-b">
          <div className="md:col-span-2">
            <label className="form-label">Buscar</label>
            <input
              className="form-input"
              placeholder="UC ou cliente"
              value={filters.search}
              onChange={(e) => setFilters((old) => ({ ...old, search: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Tipo</label>
            <select
              className="form-select"
              value={filters.tipo_cobranca}
              onChange={(e) => setFilters((old) => ({ ...old, tipo_cobranca: e.target.value }))}
            >
              <option value="TODOS">Todos</option>
              <option value="FIXO">FIXO</option>
              <option value="KWH">KWH</option>
            </select>
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={filters.status} onChange={(e) => setFilters((old) => ({ ...old, status: e.target.value }))}>
              <option value="TODOS">Todos</option>
              <option value="ATIVOS">Ativos</option>
              <option value="INATIVOS">Inativos</option>
            </select>
          </div>
          <div className="flex items-end">
            <button className="btn-secondary w-full" onClick={() => setFilters({ search: '', status: 'TODOS', tipo_cobranca: 'TODOS' })}>
              Limpar
            </button>
          </div>
        </div>
        <div className="card-header">Contratos ({filteredItems.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>UC</th>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Dia Venc.</th>
                  <th>Juros</th>
                  <th>Multa</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.apelido || item.numero_instalacao_enel}</td>
                    <td>{item.customer_nome || '-'}</td>
                    <td>{item.tipo_cobranca}</td>
                    <td>{item.dia_vencimento}</td>
                    <td>{Number(item.juros_percentual || 0).toFixed(2)}%</td>
                    <td>{Number(item.multa_percentual || 0).toFixed(2)}%</td>
                    <td>
                      <span className={item.ativo ? 'badge-success' : 'badge-danger'}>{item.ativo ? 'Ativo' : 'Inativo'}</span>
                    </td>
                    <td>
                      <button className="text-primary-600 hover:text-primary-900 p-1" onClick={() => openEdit(item)}>
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      Nenhum contrato cadastrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">{form.id ? 'Editar Contrato' : 'Novo Contrato'}</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="form-label">Unidade Consumidora</label>
                <select className="form-select" value={form.consumer_unit_id} onChange={(e) => setForm({ ...form, consumer_unit_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {(u.apelido || u.numero_instalacao_enel) + ' - ' + (u.customer_nome || '-')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Tipo Cobranca</label>
                <select className="form-select" value={form.tipo_cobranca} onChange={(e) => setForm({ ...form, tipo_cobranca: e.target.value })}>
                  <option value="FIXO">FIXO</option>
                  <option value="KWH">KWH</option>
                </select>
              </div>
              <div>
                <label className="form-label">Dia Vencimento</label>
                <input className="form-input" type="number" min="1" max="31" value={form.dia_vencimento} onChange={(e) => setForm({ ...form, dia_vencimento: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Mensalidade Fixa</label>
                <input className="form-input" type="number" step="0.01" value={form.mensalidade_fixa} onChange={(e) => setForm({ ...form, mensalidade_fixa: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Valor por kWh</label>
                <input className="form-input" type="number" step="0.0001" value={form.valor_por_kwh} onChange={(e) => setForm({ ...form, valor_por_kwh: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Minimo Mensal</label>
                <input className="form-input" type="number" step="0.01" value={form.minimo_mensal} onChange={(e) => setForm({ ...form, minimo_mensal: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Maximo Mensal</label>
                <input className="form-input" type="number" step="0.01" value={form.maximo_mensal} onChange={(e) => setForm({ ...form, maximo_mensal: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Juros %</label>
                <input className="form-input" type="number" step="0.01" value={form.juros_percentual} onChange={(e) => setForm({ ...form, juros_percentual: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Multa %</label>
                <input className="form-input" type="number" step="0.01" value={form.multa_percentual} onChange={(e) => setForm({ ...form, multa_percentual: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Inicio Vigencia</label>
                <input className="form-input" type="date" value={form.inicio_vigencia} onChange={(e) => setForm({ ...form, inicio_vigencia: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Fim Vigencia</label>
                <input className="form-input" type="date" value={form.fim_vigencia} onChange={(e) => setForm({ ...form, fim_vigencia: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
                  Contrato ativo
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={save}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarContractsPage;


